/**
 * /crm/settings/sla — admin-controlled SLA & breach policy.
 *
 * Three sections in one form:
 *
 *   A) Stage limits                (proxies SupportConfiguration)
 *   B) Breach recording grace       (CrmSlaPolicy.order_tag_grace_seconds)
 *   C) CRM ticket priority bands +  (CrmSlaPolicy.priority_*_after_seconds)
 *      optional resolve guards     (CrmSlaPolicy.require_*_for_resolution)
 *
 * Section A and Sections B/C save through different endpoints (the singleton
 * model lives in `singleton_model` and the policy in `crm`); we keep one
 * "Save changes" button that fires both PATCHes only when the local form is
 * dirty against the server snapshot, so admins see one mental "save" action.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Gauge,
  Loader2,
  Save,
  ShieldAlert,
  Timer,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useSlaPolicy,
  usePatchSlaPolicy,
  usePatchSlaStageLimits,
  type CrmSlaPolicy,
  type CrmSlaPolicyPayload,
  type CrmSlaStageLimits,
  type CrmSlaStageLimitsPayload,
} from '@/api/bi/crmSettings';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';

interface PolicyForm {
  order_tag_grace_seconds: string;
  priority_normal_after_seconds: string;
  priority_high_after_seconds: string;
  require_reason_for_resolution: boolean;
  require_evidence_for_resolution: boolean;
  min_resolution_note_length: string;
}

interface StageLimitsForm {
  sku_count_limit: string;
  packing_normal_time: string;
  packing_limit_time: string;
  biller_normal_time: string;
  biller_limit_time: string;
  picking_time: string;
  support_action_time: string;
  rider_time: string;
  secondary_rider_time: string;
  tertiary_rider_time: string;
  delivery_handover_seconds: string;
  return_entry_seconds: string;
  return_racking_seconds: string;
}

const STAGE_FIELD_KEYS: Array<keyof StageLimitsForm> = [
  'sku_count_limit',
  'packing_normal_time',
  'packing_limit_time',
  'biller_normal_time',
  'biller_limit_time',
  'picking_time',
  'support_action_time',
  'rider_time',
  'secondary_rider_time',
  'tertiary_rider_time',
  'delivery_handover_seconds',
  'return_entry_seconds',
  'return_racking_seconds',
];

function policyFromServer(p: CrmSlaPolicy | undefined): PolicyForm {
  return {
    order_tag_grace_seconds: String(p?.order_tag_grace_seconds ?? 0),
    priority_normal_after_seconds: String(p?.priority_normal_after_seconds ?? 300),
    priority_high_after_seconds: String(p?.priority_high_after_seconds ?? 1200),
    require_reason_for_resolution: p?.require_reason_for_resolution ?? false,
    require_evidence_for_resolution: p?.require_evidence_for_resolution ?? false,
    min_resolution_note_length: String(p?.min_resolution_note_length ?? 20),
  };
}

function stageLimitsFromServer(s: CrmSlaStageLimits | undefined): StageLimitsForm {
  const toStr = (v: number | null | undefined) => (v == null ? '' : String(v));
  return {
    sku_count_limit: toStr(s?.sku_count_limit),
    packing_normal_time: toStr(s?.packing_normal_time),
    packing_limit_time: toStr(s?.packing_limit_time),
    biller_normal_time: toStr(s?.biller_normal_time),
    biller_limit_time: toStr(s?.biller_limit_time),
    picking_time: toStr(s?.picking_time),
    support_action_time: toStr(s?.support_action_time),
    rider_time: toStr(s?.rider_time),
    secondary_rider_time: toStr(s?.secondary_rider_time),
    tertiary_rider_time: toStr(s?.tertiary_rider_time),
    delivery_handover_seconds: toStr(s?.delivery_handover_seconds),
    return_entry_seconds: toStr(s?.return_entry_seconds),
    return_racking_seconds: toStr(s?.return_racking_seconds),
  };
}

function parseRequiredInt(raw: string, label: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    throw new Error(`${label} must be a non-negative integer.`);
  }
  return n;
}

function parseOptionalInt(raw: string, label: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    throw new Error(`${label} must be a non-negative integer or empty.`);
  }
  return n;
}

export function CrmSlaPolicyPage() {
  const policyQuery = useSlaPolicy();
  const patchPolicy = usePatchSlaPolicy();
  const patchLimits = usePatchSlaStageLimits();

  const [policyForm, setPolicyForm] = useState<PolicyForm>(() => policyFromServer(undefined));
  const [stageForm, setStageForm] = useState<StageLimitsForm>(() => stageLimitsFromServer(undefined));

  useEffect(() => {
    if (policyQuery.data) {
      setPolicyForm(policyFromServer(policyQuery.data.policy));
      setStageForm(stageLimitsFromServer(policyQuery.data.stage_limits));
    }
  }, [policyQuery.data]);

  const policyDirty = useMemo(() => {
    const server = policyFromServer(policyQuery.data?.policy);
    return JSON.stringify(server) !== JSON.stringify(policyForm);
  }, [policyForm, policyQuery.data?.policy]);

  const stageDirty = useMemo(() => {
    const server = stageLimitsFromServer(policyQuery.data?.stage_limits);
    return JSON.stringify(server) !== JSON.stringify(stageForm);
  }, [stageForm, policyQuery.data?.stage_limits]);

  const dirty = policyDirty || stageDirty;
  const saving = patchPolicy.isPending || patchLimits.isPending;

  function setPolicyField<K extends keyof PolicyForm>(key: K, value: PolicyForm[K]) {
    setPolicyForm((f) => ({ ...f, [key]: value }));
  }

  function setStageField<K extends keyof StageLimitsForm>(key: K, value: StageLimitsForm[K]) {
    setStageForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    try {
      if (policyDirty) {
        const policyPayload: CrmSlaPolicyPayload = {
          order_tag_grace_seconds: parseRequiredInt(
            policyForm.order_tag_grace_seconds,
            'Breach grace',
          ),
          priority_normal_after_seconds: parseRequiredInt(
            policyForm.priority_normal_after_seconds,
            'Normal priority threshold',
          ),
          priority_high_after_seconds: parseRequiredInt(
            policyForm.priority_high_after_seconds,
            'High priority threshold',
          ),
          require_reason_for_resolution: policyForm.require_reason_for_resolution,
          require_evidence_for_resolution: policyForm.require_evidence_for_resolution,
          min_resolution_note_length: parseRequiredInt(
            policyForm.min_resolution_note_length,
            'Min resolution note length',
          ),
        };
        if (
          policyPayload.priority_high_after_seconds! <
          policyPayload.priority_normal_after_seconds!
        ) {
          throw new Error(
            'High priority threshold must be greater than or equal to the normal threshold.',
          );
        }
        await patchPolicy.mutateAsync(policyPayload);
      }
      if (stageDirty) {
        const limitsPayload: CrmSlaStageLimitsPayload = {};
        for (const key of STAGE_FIELD_KEYS) {
          limitsPayload[key] = parseOptionalInt(stageForm[key], key);
        }
        await patchLimits.mutateAsync(limitsPayload);
      }
      notify.success('SLA & breach policy saved.');
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save the SLA policy.'));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="SLA & Breach Policy"
        description={
          'Three knobs control how breaches become tickets: how long the operational ' +
          'stages may take, how much lateness is absorbed before a breach is recorded, ' +
          'and how lateness translates into ticket priority.'
        }
        actions={
          <Button size="sm" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1 h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        }
      />

      {policyQuery.isLoading ? (
        <Skeleton className="h-96" />
      ) : policyQuery.isError ? (
        <ErrorState
          title="Could not load SLA policy"
          description={String((policyQuery.error as Error)?.message ?? '')}
          onRetry={() => void policyQuery.refetch()}
        />
      ) : (
        <div className="grid gap-4">
          {/* ----------------------- Section A: stage limits ----------------------- */}
          <Card>
            <CardHeader>
              <CardTitle className="inline-flex items-center gap-2">
                <Timer className="h-4 w-4" /> Operational stage limits
                <Badge tone="info">SupportConfiguration</Badge>
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Times in <strong>seconds</strong>. Leave a field empty to disable the corresponding
                stage SLA. The "normal vs limit" split for packing and billing applies based on the
                SKU count threshold.
              </p>
              <div className="grid gap-3 md:grid-cols-3">
                <Field label="SKU count threshold" hint="Orders <= threshold use the 'normal' time, > threshold use the 'limit' time.">
                  <Input
                    type="number"
                    value={stageForm.sku_count_limit}
                    onChange={(e) => setStageField('sku_count_limit', e.target.value)}
                    placeholder="e.g. 15"
                  />
                </Field>
                <Field label="Packing — normal time">
                  <Input
                    type="number"
                    value={stageForm.packing_normal_time}
                    onChange={(e) => setStageField('packing_normal_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Packing — limit time (large baskets)">
                  <Input
                    type="number"
                    value={stageForm.packing_limit_time}
                    onChange={(e) => setStageField('packing_limit_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Billing — normal time">
                  <Input
                    type="number"
                    value={stageForm.biller_normal_time}
                    onChange={(e) => setStageField('biller_normal_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Billing — limit time">
                  <Input
                    type="number"
                    value={stageForm.biller_limit_time}
                    onChange={(e) => setStageField('biller_limit_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Picking time (rider)">
                  <Input
                    type="number"
                    value={stageForm.picking_time}
                    onChange={(e) => setStageField('picking_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Reaching customer (rider)" hint="Multiplied by 1.5 for cycle riders automatically.">
                  <Input
                    type="number"
                    value={stageForm.rider_time}
                    onChange={(e) => setStageField('rider_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Secondary rider time">
                  <Input
                    type="number"
                    value={stageForm.secondary_rider_time}
                    onChange={(e) => setStageField('secondary_rider_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Tertiary rider time">
                  <Input
                    type="number"
                    value={stageForm.tertiary_rider_time}
                    onChange={(e) => setStageField('tertiary_rider_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field label="Support action time" hint="Used by support workflow timers, optional.">
                  <Input
                    type="number"
                    value={stageForm.support_action_time}
                    onChange={(e) => setStageField('support_action_time', e.target.value)}
                    placeholder="seconds"
                  />
                </Field>
                <Field
                  label="Delivery handover (reached → completed)"
                  hint="Requires reached_timestamp; null disables delivery_late tag."
                >
                  <Input
                    type="number"
                    value={stageForm.delivery_handover_seconds}
                    onChange={(e) =>
                      setStageField('delivery_handover_seconds', e.target.value)
                    }
                    placeholder="seconds"
                  />
                </Field>
                <Field
                  label="Return entry"
                  hint="Rider returned to store → biller processed return; picked orders only."
                >
                  <Input
                    type="number"
                    value={stageForm.return_entry_seconds}
                    onChange={(e) =>
                      setStageField('return_entry_seconds', e.target.value)
                    }
                    placeholder="seconds"
                  />
                </Field>
                <Field
                  label="Return racking"
                  hint="Biller processed return → packer racked/restored returned units; picked orders only."
                >
                  <Input
                    type="number"
                    value={stageForm.return_racking_seconds}
                    onChange={(e) =>
                      setStageField('return_racking_seconds', e.target.value)
                    }
                    placeholder="seconds"
                  />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* ----------------------- Section B: grace ----------------------- */}
          <Card>
            <CardHeader>
              <CardTitle className="inline-flex items-center gap-2">
                <Gauge className="h-4 w-4" /> Breach recording grace
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Lateness below this threshold is absorbed silently — no <code>OrderTag</code> is
                written and no auto-ticket is opened. Use this to ignore single-second jitters
                without touching stage limits.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Grace seconds" hint="0 = legacy behaviour: any positive lateness creates a tag.">
                  <Input
                    type="number"
                    value={policyForm.order_tag_grace_seconds}
                    onChange={(e) =>
                      setPolicyField('order_tag_grace_seconds', e.target.value)
                    }
                    placeholder="0"
                  />
                </Field>
                <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-[11px] text-warning-soft">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Increasing grace changes
                  historical comparability — your analytics dashboards stamp the active policy
                  version so trends stay honest.
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ----------------------- Section C: priority + resolve guards ----------------------- */}
          <Card>
            <CardHeader>
              <CardTitle className="inline-flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" /> Ticket priority &amp; resolve guards
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <p className="text-xs text-muted-foreground">
                When the auto-ticket pipeline opens an SLA breach ticket, the lateness on the tag
                determines the initial priority. Resolve guards optionally block close-out without
                a populated reason or evidence.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Priority = normal at lateness >=" hint="Below this it stays low.">
                  <Input
                    type="number"
                    value={policyForm.priority_normal_after_seconds}
                    onChange={(e) =>
                      setPolicyField('priority_normal_after_seconds', e.target.value)
                    }
                    placeholder="300"
                  />
                </Field>
                <Field label="Priority = high at lateness >=" hint="Must be >= the normal threshold.">
                  <Input
                    type="number"
                    value={policyForm.priority_high_after_seconds}
                    onChange={(e) =>
                      setPolicyField('priority_high_after_seconds', e.target.value)
                    }
                    placeholder="1200"
                  />
                </Field>
              </div>

              <div className="space-y-2 rounded-md border border-border bg-surface p-3">
                <label className="flex items-start gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={policyForm.require_reason_for_resolution}
                    onChange={(e) =>
                      setPolicyField('require_reason_for_resolution', e.target.checked)
                    }
                  />
                  <span>
                    <strong>Require reason on resolve.</strong> Block resolve on{' '}
                    <code>sla_breach</code> tickets without a populated reason. Sub-reason is
                    required when the reason has children, so analytics never see "Other / blank".
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={policyForm.require_evidence_for_resolution}
                    onChange={(e) =>
                      setPolicyField('require_evidence_for_resolution', e.target.checked)
                    }
                  />
                  <span>
                    <strong>Require evidence on resolve.</strong> Either an attached call recording
                    or a resolution note of at least the configured length.
                  </span>
                </label>
                <Field label="Min resolution note length (chars)">
                  <Input
                    type="number"
                    value={policyForm.min_resolution_note_length}
                    onChange={(e) =>
                      setPolicyField('min_resolution_note_length', e.target.value)
                    }
                    placeholder="20"
                  />
                </Field>
              </div>
            </CardBody>
          </Card>

          {policyQuery.data?.policy.effective_from ? (
            <p className="text-[11px] text-muted-foreground">
              Current policy effective since{' '}
              <strong>{new Date(policyQuery.data.policy.effective_from).toLocaleString()}</strong>.
              Saving a change re-stamps this timestamp so analytics dashboards can split metrics by
              policy version.
            </p>
          ) : null}
        </div>
      )}
    </>
  );
}
